import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, FormControl, FormControlLabel, InputAdornment, InputLabel,
  LinearProgress, Link, MenuItem, Pagination, Select, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import BalanceIcon from "@mui/icons-material/Balance";
import PhoneIcon from "@mui/icons-material/Phone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  buscarProveedores, exportarOportunidades, exportarProveedores, getCambios,
  getOportunidades, getSancionados,
  Cambios, FiltrosProveedores, Oportunidad, Oportunidades, ProveedorDetalle, ProveedorResumen,
} from "../services/oece.service";
import { C, EstadoProveedor, Pill, TiposRnp, Vacio, fecha, fechaHora, gridSx, num } from "./oeceUi";

const localeText = esES.components.MuiDataGrid.defaultProps.localeText;

interface TabProps {
  onVerFicha: (ruc: string) => void;
}

/* Celda "razón social + RUC" que usan todas las tablas */
const celdaProveedor = (razon: string | null, ruc: string) => (
  <Box sx={{ minWidth: 0, py: 1 }}>
    <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.5, color: C.navy }} title={razon ?? ""}>
      {razon || "Sin razón social"}
    </Typography>
    <Typography sx={{ fontSize: 12, color: C.muted, fontFamily: "monospace" }}>{ruc}</Typography>
  </Box>
);

export const celdaContacto = (emails: string[], telefonos: string[]) =>
  emails.length + telefonos.length === 0 ? (
    <Typography variant="caption" color="text.disabled">—</Typography>
  ) : (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: C.muted }}>
      {telefonos.length > 0 && (
        <Tooltip title={telefonos.join(" · ")}>
          <Stack direction="row" spacing={0.25} alignItems="center">
            <PhoneIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 12.5 }}>{telefonos[0]}</Typography>
          </Stack>
        </Tooltip>
      )}
      {emails.length > 0 && (
        <Tooltip title={emails.join(" · ")}>
          <MailOutlineIcon sx={{ fontSize: 17 }} />
        </Tooltip>
      )}
    </Stack>
  );

export const BotonExportar = ({ onClick }: { onClick: () => Promise<void> }) => {
  const [bajando, setBajando] = useState(false);
  return (
    <Button
      variant="outlined"
      startIcon={<FileDownloadOutlinedIcon />}
      disabled={bajando}
      onClick={async () => {
        setBajando(true);
        try { await onClick(); } finally { setBajando(false); }
      }}
      sx={{ borderColor: C.border, color: C.navy2, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
    >
      {bajando ? "Generando…" : "Exportar Excel"}
    </Button>
  );
};

/* ═════════════════════ OPORTUNIDADES ═════════════════════ */
export const OportunidadesTab = ({ onVerFicha }: TabProps) => {
  const [data, setData] = useState<Oportunidades | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [etapa, setEtapa] = useState<"" | "reconsideracion" | "contencioso">("");
  const [multas, setMultas] = useState(false);

  useEffect(() => {
    setCargando(true);
    setError("");
    getOportunidades({ incluir_multas: multas })
      .then((r) => setData(r.data))
      .catch(() => setError("No se pudieron cargar las oportunidades."))
      .finally(() => setCargando(false));
  }, [multas]);

  const filas = useMemo(
    () => (data?.items ?? []).filter((o) => !etapa || o.etapa === etapa),
    [data, etapa]
  );

  const plazoRec = data?.plazos.reconsideracion_dias_habiles ?? 15;
  const mesesCont = data?.plazos.contencioso_meses ?? 3;

  const columnas: GridColDef<Oportunidad>[] = [
    {
      field: "etapa", headerName: "Ventana", width: 170,
      renderCell: ({ row }) =>
        row.etapa === "reconsideracion" ? (
          <Stack spacing={0.25}>
            <Pill tono="red" icon={<BoltIcon />}>Reconsideración</Pill>
            <Typography sx={{ fontSize: 11.5, color: C.red, fontWeight: 600, pl: 0.5 }}>
              Día {row.dias_habiles_desde_resolucion} de {plazoRec} hábiles
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={0.25}>
            <Pill tono="gold" icon={<BalanceIcon />}>Contencioso</Pill>
            <Typography sx={{ fontSize: 11.5, color: C.muted, pl: 0.5 }}>
              Quedan {row.dias_para_limite_contencioso} días
            </Typography>
            {row.reconsideracion_resuelta && (
              <Tooltip title={`Ya presentó reconsideración contra la ${row.resolucion_sancion ?? "sanción"} y fue ${(row.resultado_reconsideracion ?? "resuelta").toLowerCase()}. Solo queda la vía judicial.`}>
                <Typography sx={{ fontSize: 11, color: "#8a6a1c", pl: 0.5, fontWeight: 600 }}>
                  Recons. {(row.resultado_reconsideracion ?? "resuelta").toLowerCase()}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        ),
    },
    {
      field: "razon_social", headerName: "Proveedor sancionado", flex: 1.6, minWidth: 220,
      renderCell: ({ row }) => celdaProveedor(row.razon_social, row.ruc),
    },
    {
      field: "resolucion", headerName: "Resolución", width: 200,
      renderCell: ({ row }) => (
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.resolucion}</Typography>
            {row.url_resolucion && (
              <Tooltip title="Ver resolución (PDF)">
                <Link href={row.url_resolucion} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} sx={{ display: "flex", color: C.blue }}>
                  <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />
                </Link>
              </Tooltip>
            )}
          </Stack>
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            {row.expediente ? `Exp. ${row.expediente}` : fecha(row.fecha_resolucion)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "tipo_sancion", headerName: "Sanción", width: 150,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.tipo_sancion}</Typography>
          <Typography sx={{ fontSize: 12, color: C.muted }}>{row.monto_multa || row.periodo}</Typography>
        </Box>
      ),
    },
    {
      field: "motivos", headerName: "Infracción", flex: 2, minWidth: 240, sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={row.motivos.join(" · ")}>
          <Typography sx={{ fontSize: 12.5, color: "#334155", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {row.motivos[0] || "—"}
            {row.motivos.length > 1 && <b> +{row.motivos.length - 1}</b>}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "dias_para_limite_contencioso", headerName: "Plazo", width: 130,
      renderCell: ({ row }) => {
        // Porcentaje de plazo que queda en la ventana de esa fila
        const pct = row.etapa === "reconsideracion"
          ? ((plazoRec - row.dias_habiles_desde_resolucion) / plazoRec) * 100
          : Math.max(0, Math.min(100, (row.dias_para_limite_contencioso / (mesesCont * 30)) * 100));
        return (
          <Box sx={{ width: "100%" }}>
            <LinearProgress
              variant="determinate" value={pct}
              sx={{
                height: 6, borderRadius: 3, bgcolor: "#edf0f5",
                "& .MuiLinearProgress-bar": { bgcolor: pct < 25 ? C.red : pct < 60 ? C.amber : C.green },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "contacto", headerName: "Contacto", width: 150, sortable: false,
      renderCell: ({ row }) => celdaContacto(row.emails, row.telefonos),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2.5 }}>
        {[
          {
            key: "reconsideracion" as const, titulo: "En plazo de reconsideración",
            detalle: `Resolución de hace ${plazoRec} días hábiles o menos. Hay que actuar ya.`,
            n: data?.por_etapa.reconsideracion, color: C.red, bg: C.redSoft, icono: <BoltIcon />,
          },
          {
            key: "contencioso" as const, titulo: "En plazo contencioso-administrativo",
            detalle: `Todavía dentro de los ${mesesCont} meses para demandar.`,
            n: data?.por_etapa.contencioso, color: "#8a6a1c", bg: C.goldSoft, icono: <BalanceIcon />,
          },
        ].map((k) => {
          const activo = etapa === k.key;
          return (
            <Box
              key={k.key}
              onClick={() => setEtapa(activo ? "" : k.key)}
              sx={{
                cursor: "pointer", borderRadius: 3, p: 2.25, display: "flex", gap: 2, alignItems: "center",
                bgcolor: activo ? k.bg : "#fff", border: `1.5px solid ${activo ? k.color : C.border}`,
                transition: "all .15s", "&:hover": { borderColor: k.color },
              }}
            >
              <Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: k.bg, color: k.color, display: "grid", placeItems: "center" }}>
                {k.icono}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, color: C.navy }}>{k.titulo}</Typography>
                <Typography sx={{ fontSize: 13, color: C.muted }}>{k.detalle}</Typography>
              </Box>
              <Typography sx={{ fontSize: 34, fontWeight: 800, color: k.color }}>{k.n ?? "…"}</Typography>
            </Box>
          );
        })}
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {etapa && <Chip label="Quitar filtro de ventana" onDelete={() => setEtapa("")} size="small" />}
          <FormControlLabel
            control={<Switch checked={multas} onChange={(e) => setMultas(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: 14 }}>Incluir multas</Typography>}
          />
        </Stack>
        <BotonExportar onClick={() => exportarOportunidades({ etapa: etapa || undefined, incluir_multas: multas })} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <DataGrid
        rows={filas}
        columns={columnas}
        getRowId={(r) => `${r.ruc}-${r.resolucion}`}
        loading={cargando}
        rowHeight={68}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={({ row }) => onVerFicha(row.ruc)}
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        pageSizeOptions={[25, 50, 100]}
        localeText={localeText}
        sx={gridSx}
      />

      <Typography sx={{ fontSize: 12, color: C.muted, mt: 1.5 }}>
        Plazos aproximados: se cuentan desde la fecha de la resolución, no desde la notificación, y los días
        hábiles no descuentan feriados. Confirmar la fecha de notificación antes de presentar el recurso.
      </Typography>
    </Box>
  );
};

/* ═════════════════════ BUSCADOR ═════════════════════ */
type Flag = "rnp_vigente" | "habilitado" | "sancionado" | "con_sanciones" | "con_penalidades" | "con_contacto";

const FILTROS_RAPIDOS: { key: Flag; label: string; valor: boolean }[] = [
  { key: "rnp_vigente", label: "RNP vigente", valor: true },
  { key: "habilitado", label: "Habilitados", valor: true },
  { key: "habilitado", label: "No habilitados", valor: false },
  { key: "sancionado", label: "Sanción vigente", valor: true },
  { key: "con_sanciones", label: "Con antecedentes", valor: true },
  { key: "con_penalidades", label: "Con penalidades", valor: true },
  { key: "con_contacto", label: "Con contacto", valor: true },
];

export const BuscadorTab = ({ onVerFicha }: TabProps) => {
  const [texto, setTexto] = useState("");
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const [orden, setOrden] = useState("relevancia");
  const [flags, setFlags] = useState<Partial<Record<Flag, boolean>>>({});
  const [pag, setPag] = useState({ page: 0, pageSize: 25 });
  const [filas, setFilas] = useState<ProveedorResumen[]>([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);

  // Espera a que el usuario deje de escribir
  useEffect(() => {
    const t = setTimeout(() => { setQ(texto.trim()); setPag((p) => ({ ...p, page: 0 })); }, 400);
    return () => clearTimeout(t);
  }, [texto]);

  const filtros: FiltrosProveedores = useMemo(
    () => ({ q: q || undefined, tipo: tipo || undefined, orden, ...flags }),
    [q, tipo, orden, flags]
  );

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    buscarProveedores({ ...filtros, page: pag.page + 1, size: pag.pageSize })
      .then((r) => { if (vigente) { setFilas(r.data.items); setTotal(r.data.total); } })
      .catch(() => { if (vigente) { setFilas([]); setTotal(0); } })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [filtros, pag]);

  const alternar = (key: Flag, valor: boolean) => {
    setFlags((f) => {
      const n = { ...f };
      if (n[key] === valor) delete n[key];
      else n[key] = valor;
      return n;
    });
    setPag((p) => ({ ...p, page: 0 }));
  };

  const columnas: GridColDef<ProveedorResumen>[] = [
    {
      field: "razon_social", headerName: "Proveedor", flex: 2, minWidth: 260, sortable: false,
      renderCell: ({ row }) => celdaProveedor(row.razon_social, row.ruc),
    },
    {
      field: "estado", headerName: "Estado", width: 140, sortable: false,
      renderCell: ({ row }) => <EstadoProveedor habilitado={row.habilitado} sancionado={row.antecedentes.alguna_vigente} />,
    },
    {
      field: "rnp", headerName: "RNP vigente", width: 170, sortable: false,
      renderCell: ({ row }) => <TiposRnp tipos={row.rnp.tipos_vigentes} />,
    },
    {
      field: "sanciones", headerName: "Sanciones", width: 105, sortable: false, align: "center", headerAlign: "center",
      renderCell: ({ row }) => {
        const n = row.antecedentes.sanciones + row.antecedentes.inhab_judicial + row.antecedentes.inhab_administrativa;
        return n ? <Pill tono={row.antecedentes.alguna_vigente ? "red" : "gray"}>{n}</Pill> : <Typography color="text.disabled">0</Typography>;
      },
    },
    {
      field: "penalidades", headerName: "Penalidades", width: 110, sortable: false, align: "center", headerAlign: "center",
      renderCell: ({ row }) =>
        row.antecedentes.penalidades ? <Pill tono="amber">{row.antecedentes.penalidades}</Pill> : <Typography color="text.disabled">0</Typography>,
    },
    {
      field: "ultimo_cambio", headerName: "Actualizado", width: 150, sortable: false,
      renderCell: ({ row }) => <Typography sx={{ fontSize: 12.5, color: C.muted }}>{fechaHora(row.ultimo_cambio)}</Typography>,
    },
  ];

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Buscar por RUC o razón social…"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.muted }} /></InputAdornment>,
            sx: { bgcolor: "#fff", borderRadius: 2.5 },
          }}
        />
        <FormControl sx={{ minWidth: 190 }}>
          <InputLabel>Tipo de registro</InputLabel>
          <Select value={tipo} label="Tipo de registro" onChange={(e) => { setTipo(e.target.value); setPag((p) => ({ ...p, page: 0 })); }} sx={{ bgcolor: "#fff", borderRadius: 2.5 }}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="1">Bienes</MenuItem>
            <MenuItem value="2">Servicios</MenuItem>
            <MenuItem value="3">Consultor de obras</MenuItem>
            <MenuItem value="4">Ejecutor de obras</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 170 }}>
          <InputLabel>Ordenar por</InputLabel>
          <Select value={orden} label="Ordenar por" onChange={(e) => setOrden(e.target.value)} sx={{ bgcolor: "#fff", borderRadius: 2.5 }}>
            <MenuItem value="relevancia">Relevancia</MenuItem>
            <MenuItem value="razon_social">Razón social</MenuItem>
            <MenuItem value="ruc">RUC</MenuItem>
            <MenuItem value="cambiado">Cambios recientes</MenuItem>
            <MenuItem value="sanciones">Más sanciones</MenuItem>
            <MenuItem value="penalidades">Más penalidades</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {FILTROS_RAPIDOS.map((f) => {
            const activo = flags[f.key] === f.valor;
            return (
              <Chip
                key={f.label}
                label={f.label}
                onClick={() => alternar(f.key, f.valor)}
                variant={activo ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600, borderColor: C.border,
                  bgcolor: activo ? C.navy2 : "#fff", color: activo ? "#fff" : C.navy,
                  "&:hover": { bgcolor: activo ? C.navy : "#eef2f8" },
                }}
              />
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ fontSize: 13, color: C.muted, whiteSpace: "nowrap" }}>
            <b style={{ color: C.navy }}>{num(total)}</b> resultados
          </Typography>
          <BotonExportar onClick={() => exportarProveedores(filtros)} />
        </Stack>
      </Stack>

      <DataGrid
        rows={filas}
        columns={columnas}
        getRowId={(r) => r.ruc}
        loading={cargando}
        rowHeight={60}
        autoHeight
        disableRowSelectionOnClick
        disableColumnMenu
        onRowClick={({ row }) => onVerFicha(row.ruc)}
        paginationMode="server"
        rowCount={total}
        paginationModel={pag}
        onPaginationModelChange={setPag}
        pageSizeOptions={[25, 50, 100]}
        localeText={localeText}
        sx={gridSx}
      />
    </Box>
  );
};

/* ═════════════════════ SANCIONADOS ═════════════════════ */
export const SancionadosTab = ({ onVerFicha }: TabProps) => {
  const [pag, setPag] = useState({ page: 0, pageSize: 25 });
  const [filas, setFilas] = useState<ProveedorDetalle[]>([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    setCargando(true);
    getSancionados(pag.page + 1, pag.pageSize)
      .then((r) => { setFilas(r.data.items); setTotal(r.data.total); })
      .catch(() => setFilas([]))
      .finally(() => setCargando(false));
  }, [pag]);

  const principal = (r: ProveedorDetalle) => r.vigentes?.[0];

  const columnas: GridColDef<ProveedorDetalle>[] = [
    {
      field: "razon_social", headerName: "Proveedor", flex: 1.6, minWidth: 230, sortable: false,
      renderCell: ({ row }) => celdaProveedor(row.razon_social, row.ruc),
    },
    {
      field: "sancion", headerName: "Sanción vigente", width: 190, sortable: false,
      renderCell: ({ row }) => {
        const s = principal(row);
        const extra = (row.vigentes?.length ?? 0) - 1;
        return (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.red }}>
              {s?.nroRes ? s.descripcion : s?.tipo === "inhab_judicial" ? "INHAB. JUDICIAL" : "INHABILITACIÓN"}
              {extra > 0 && <span style={{ color: C.muted, fontWeight: 500 }}> +{extra}</span>}
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.muted }}>{s?.periodo || s?.nroRes || ""}</Typography>
          </Box>
        );
      },
    },
    {
      field: "vigencia", headerName: "Vigencia", width: 190, sortable: false,
      renderCell: ({ row }) => {
        const s = principal(row);
        const fin = s?.fechaFin && s.fechaFin !== "null" ? fecha(s.fechaFin) : "Indefinida";
        return (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ fontSize: 12.5, color: "#334155" }}>
            <span>{s?.fechaIni ? fecha(s.fechaIni) : "—"}</span>
            <ArrowRightAltIcon sx={{ fontSize: 16, color: C.muted }} />
            <b>{fin}</b>
          </Stack>
        );
      },
    },
    {
      field: "motivo", headerName: "Infracción", flex: 2, minWidth: 240, sortable: false,
      renderCell: ({ row }) => {
        const s = principal(row);
        const texto = s?.motivos?.[0] || (s?.nroRes ? "" : s?.descripcion) || "—";
        return (
          <Tooltip title={s?.motivos?.join(" · ") || texto}>
            <Typography sx={{ fontSize: 12.5, color: "#334155", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {texto}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "contacto", headerName: "Contacto", width: 150, sortable: false,
      renderCell: ({ row }) => celdaContacto(row.contacto.emails, row.contacto.telefonos),
    },
  ];

  return (
    <Box>
      <Typography sx={{ fontSize: 13.5, color: C.muted, mb: 1.5 }}>
        <b style={{ color: C.navy }}>{num(total)}</b> proveedores con sanción o inhabilitación vigente, los más recientes primero.
      </Typography>
      <DataGrid
        rows={filas}
        columns={columnas}
        getRowId={(r) => r.ruc}
        loading={cargando}
        rowHeight={64}
        autoHeight
        disableRowSelectionOnClick
        disableColumnMenu
        onRowClick={({ row }) => onVerFicha(row.ruc)}
        paginationMode="server"
        rowCount={total}
        paginationModel={pag}
        onPaginationModelChange={setPag}
        pageSizeOptions={[25, 50, 100]}
        localeText={localeText}
        sx={gridSx}
      />
    </Box>
  );
};

/* ═════════════════════ CAMBIOS ═════════════════════ */
const CAMPOS: Record<string, string> = {
  razon_social: "Razón social",
  rnp_registrado: "RNP registrado",
  rnp_vigente: "RNP vigente",
  rnp_tipos_vigentes: "Registros RNP vigentes",
  habilitado: "Habilitado",
  apto_contratar: "Apto para contratar",
  num_sanciones: "Sanciones",
  num_inhab_judicial: "Inhab. judiciales",
  num_inhab_administrativa: "Inhab. administrativas",
  num_medidas_cautelares: "Medidas cautelares",
  num_penalidades: "Penalidades",
  algo_vigente: "Sanción vigente",
};

const NOMBRE_TIPO: Record<string, string> = { "1": "Bienes", "2": "Servicios", "3": "Consultor", "4": "Ejecutor" };

const valorLegible = (campo: string, v: unknown) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (Array.isArray(v)) return campo === "rnp_tipos_vigentes" ? (v.map((c) => NOMBRE_TIPO[c] ?? c).join(", ") || "Ninguno") : v.join(", ");
  return String(v);
};

// ¿El cambio es una mala noticia para el proveedor? (sirve para el color)
const esNegativo = (campo: string, antes: unknown, ahora: unknown) => {
  if (campo === "algo_vigente") return ahora === true;
  if (campo.startsWith("num_")) return Number(ahora) > Number(antes);
  if (typeof ahora === "boolean") return ahora === false;
  if (Array.isArray(ahora) && Array.isArray(antes)) return ahora.length < antes.length;
  return false;
};

const RANGOS = [
  { dias: 1, label: "Últimas 24 h" },
  { dias: 7, label: "Últimos 7 días" },
  { dias: 30, label: "Últimos 30 días" },
];

export const CambiosTab = ({ onVerFicha }: TabProps) => {
  const [dias, setDias] = useState(7);
  const [campo, setCampo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Cambios | null>(null);
  const [cargando, setCargando] = useState(false);
  const size = 30;

  useEffect(() => {
    setCargando(true);
    const desde = new Date(Date.now() - dias * 86400000).toISOString();
    getCambios({ desde, campo: campo || undefined, page, size })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, [dias, campo, page]);

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={0.75}>
          {RANGOS.map((r) => (
            <Chip
              key={r.dias}
              label={r.label}
              onClick={() => { setDias(r.dias); setPage(1); }}
              sx={{
                fontWeight: 600,
                bgcolor: dias === r.dias ? C.navy2 : "#fff",
                color: dias === r.dias ? "#fff" : C.navy,
                border: `1px solid ${dias === r.dias ? C.navy2 : C.border}`,
                "&:hover": { bgcolor: dias === r.dias ? C.navy : "#eef2f8" },
              }}
            />
          ))}
        </Stack>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Tipo de cambio</InputLabel>
          <Select value={campo} label="Tipo de cambio" onChange={(e) => { setCampo(e.target.value); setPage(1); }} sx={{ bgcolor: "#fff" }}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(CAMPOS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 13, color: C.muted }}>
          <b style={{ color: C.navy }}>{num(data?.total)}</b> proveedores con cambios
        </Typography>
      </Stack>

      {cargando && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {!cargando && data && data.items.length === 0 && (
        <Vacio texto="Sin cambios en este periodo. El historial se llena a partir de la segunda corrida diaria." />
      )}

      <Stack spacing={1.25}>
        {data?.items.map((c) => (
          <Box
            key={`${c.ruc}-${c.fecha}`}
            onClick={() => onVerFicha(c.ruc)}
            sx={{
              bgcolor: "#fff", border: `1px solid ${C.border}`, borderRadius: 2.5, p: 2, cursor: "pointer",
              display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(240px, 1fr) 2fr" }, gap: 2,
              transition: "border-color .15s, box-shadow .15s",
              "&:hover": { borderColor: "#c3cfe0", boxShadow: "0 2px 10px rgba(15, 30, 60, .06)" },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {celdaProveedor(c.razon_social, c.ruc)}
              <Typography sx={{ fontSize: 11.5, color: C.muted }}>{fechaHora(c.fecha)}</Typography>
            </Box>
            <Stack spacing={0.75} justifyContent="center">
              {Object.entries(c.cambios).map(([k, v]) => {
                const malo = esNegativo(k, v.antes, v.ahora);
                return (
                  <Stack key={k} direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.navy, minWidth: 170 }}>{CAMPOS[k] ?? k}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: C.muted, textDecoration: "line-through" }}>{valorLegible(k, v.antes)}</Typography>
                    <ArrowRightAltIcon sx={{ fontSize: 18, color: C.muted }} />
                    <Pill tono={malo ? "red" : "green"}>{valorLegible(k, v.ahora)}</Pill>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>

      {data && data.total > size && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
          <Pagination count={Math.ceil(data.total / size)} page={page} onChange={(_, p) => setPage(p)} shape="rounded" />
        </Box>
      )}
    </Box>
  );
};
